import ColorMapLegend from "components/ColorMapLegend/ColorMapLegend";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";
import useConvertValueToUserUnit from "hooks/useConvertValueToUserUnit";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useStores } from "stores/setup/use-store";

interface ForecastColorMapProps {
  environmentalVariable: string;
}

const ForecastColorMap: React.FC<ForecastColorMapProps> = ({
  environmentalVariable,
}) => {
  const {
    dataStores: { forecastMapStore },
  } = useStores();

  const { getUnitUser, convertToUserUnit } = useConvertValueToUserUnit({
    environmentalVariable,
    defaultUnit: forecastMapStore.layersData?.colormap?.unitSymbol,
  });

  const unitUser = useMemo(() => {
    return getUnitUser(environmentalVariable) as string;
  }, [environmentalVariable, getUnitUser]);

  const shouldConvertUnit = useMemo(() => {
    return (
      unitUser &&
      forecastMapStore.layersData?.colormap?.unitSymbol &&
      unitUser !== forecastMapStore.layersData.colormap.unitSymbol
    );
  }, [unitUser, forecastMapStore.layersData?.colormap?.unitSymbol]);

  const colormap = useMemo(() => {
    const original = forecastMapStore.layersData?.colormap;
    if (!original) return null;

    return {
      ...original,
      displayValues: original.displayValues?.map((val) =>
        shouldConvertUnit ? (convertToUserUnit(val) as number) : val
      ),

      colormap: original.colormap?.map((item) => {
        const decimal =
          environmentalVariable === EnvironmentalVariables.PRECIP
            ? unitUser === "in"
              ? 2
              : 0
            : 0;
        const value = shouldConvertUnit
          ? convertToUserUnit(item.value)?.toFixed(decimal)
          : 0;

        return {
          ...item,
          value: shouldConvertUnit && value ? parseFloat(value) : item.value,
        };
      }),
    };
  }, [
    forecastMapStore.layersData?.colormap,
    shouldConvertUnit,
    convertToUserUnit,
    environmentalVariable,
    unitUser,
  ]);

  if (!colormap) {
    return null;
  }

  return (
    <ColorMapLegend
      colorMap={colormap.colormap}
      displayValues={colormap.displayValues}
      unit={unitUser || colormap.unitSymbol}
      humanizedScale={colormap.humanizedScale}
    />
  );
};

export default observer(ForecastColorMap);
